// Item 21 part 2 (Carla, 2026-10-20): the page the "If it wasn't you, secure your account" link in
// the notice to the OLD address opens. It posts the token ONCE to POST
// /api/auth/revert-email-change, which in one step puts the old address back, signs out every
// device and hands back a one-time password-reset token; the page then offers the existing
// /reset-password step so the person chooses a new password straight away. Refusals (not valid,
// expired, already used, cannot be put back) are said in the server's words, which name support.
// It works signed in or not (the old mailbox is the proof), so AppWrapper answers it before sign-in.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { messageFromError } from '../utils/apiError';
import { PageLayout, PageHeader, Card, Banner, ErrorState, Skeleton } from '../ui';

const TITLES = {
  missing_token: 'This link is incomplete',
  invalid_token: 'This link can\'t be used',
  expired_token: 'This link has expired',
  used_token: 'This link has already been used',
  email_conflict: 'Your email could not be put back',
};

export default function RevertEmailChange() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const [state, setState] = useState(token ? { status: 'working' } : { status: 'failed', code: 'missing_token', message: 'There is no code in this link. Open the link from the email again, or email support@aideyapp.com.' });
  const request = useRef(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    if (!request.current) {
      request.current = api('/api/auth/revert-email-change', { method: 'POST', body: JSON.stringify({ token }) })
        .then((res) => res.json());
    }
    request.current.then(
      (data) => { if (alive) setState({ status: 'done', email: data?.email || '', resetToken: data?.resetToken || '' }); },
      (err) => { if (alive) setState({ status: 'failed', code: err?.body?.code || null, message: messageFromError(err, 'Your email could not be put back just now.') }); },
    );
    return () => { alive = false; };
  }, [token, attempt]);

  // Only a failure that changed nothing (no answer, a server fault, a rate limit) is tried again.
  const retryable = state.status === 'failed' && (!state.code || state.code === 'server_error' || state.code === 'rate_limited');
  const retry = () => { request.current = null; setState({ status: 'working' }); setAttempt((n) => n + 1); };

  return (
    <PageLayout data-page="revert-email-change">
      <PageHeader eyebrow={{ label: 'Account', color: 'pastel-neutral' }} title="Secure your account" />
      <Card color="pastel-blue" label="Sign-in email" lift={false}>
        {state.status === 'working' && (
          <div data-testid="rec-working" aria-busy="true">
            <p>Putting your email back…</p>
            <Skeleton />
          </div>
        )}
        {state.status === 'done' && (
          <div data-testid="rec-done">
            <Banner tone="success">{`Your Aidey sign-in email has been put back to ${state.email} and every device has been signed out. Choose a new password.`}</Banner>
            <p>The button below works once and lasts an hour. If it runs out, use “Forgot password” on the sign-in page.</p>
            <div>
              <a className="ui-btn ui-btn--confirm" href={`/reset-password?token=${encodeURIComponent(state.resetToken)}`}>Choose a new password</a>
            </div>
          </div>
        )}
        {state.status === 'failed' && (
          <div data-testid="rec-failed">
            <ErrorState title={TITLES[state.code] || 'Your email could not be put back'} message={state.message}
              onRetry={retryable ? retry : undefined} />
          </div>
        )}
      </Card>
    </PageLayout>
  );
}
